import React, { useMemo } from 'react';
import './DashboardView.css';
import KPIBox from './KPIBox';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { formatCurrencyShort } from '../utils/format';
import PieChartComponent from './PieChartComponent';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Tooltip, Legend);

/* Palette */
const ACCENT = '#2563EB';  // Revenue
const EXP    = '#334155';  // Expenses
const NET    = '#111827';  // Net Income
const GRID   = 'rgba(229,231,235,0.2)';

/* Combo (Revenue, Expenses, Net)
   Mobile (<=600px): show last 6 points (typically 6 months)
   Desktop: show last 12 points (typically 12 months)
*/
function toComboChart(labels, revenue, expenses, net) {
  // Determine viewport once at render-time; fall back to desktop if window is undefined
  let limit = 12;
  try {
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches) {
      limit = 6;
    }
  } catch (_) {
    // no-op (SSR / safety)
  }

  // Take the last N points without mutating original arrays
  const sliceLast = (arr, n) => (Array.isArray(arr) ? arr.slice(-n) : arr);
  const L = sliceLast(labels,   limit);
  const R = sliceLast(revenue,  limit);
  const E = sliceLast(expenses, limit);
  const N = sliceLast(net,      limit);

  return {
    data: {
      labels: L,
      datasets: [
        {
          type: 'bar',
          label: 'Revenue',
          data: R,
          // keep your existing styling options exactly as before:
          backgroundColor: '#3b82f6',
          borderColor: '#3b82f6',
          borderWidth: 1,
          barThickness: 14,
          barPercentage: 0.5,
          categoryPercentage: 0.5,
          order: 1
        },
        {
          type: 'bar',
          label: 'Expenses',
          data: E,
          // Clairifi navy
          backgroundColor: '#14396dff',
          borderColor: '#14396dff',
          borderWidth: 1,
          barThickness: 14,
          barPercentage: 0.5,
          categoryPercentage: 0.5,
          order: 1
        },

        {
          type: 'line',
          label: 'Net Income',
          data: N,
          borderColor: '#111827',
          backgroundColor: '#111827',
          pointRadius: 2,
          borderWidth: 2,
          tension: 0.3,
          order: 0
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { position: 'top' },
        tooltip: { enabled: true }
      },
      scales: {
        x: { stacked: false, ticks: { maxRotation: 0, autoSkip: true } },
        y: { beginAtZero: true }
      }
    }
  };
}

/* Horizontal bar config (mix charts) */
function toHBar(labels, values) {
  return {
    data: {
      labels,
      datasets: [{ label: 'Value', data: values, backgroundColor: ACCENT, borderWidth: 0, barThickness: 18, categoryPercentage: 0.8 }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y',
      plugins: {
        legend: { display: false },
        tooltip: { callbacks: { label: (ctx) => ` ${formatCurrencyShort(ctx.parsed.x)}` } }
      },
      scales: {
        x: { grid: { color: GRID }, ticks: { callback: (v) => formatCurrencyShort(v) } },
        y: { grid: { display: false } }
      },
      layout: { padding: 12 }
    }
  };
}

export default function DashboardView({ payload }) {
  /* === Normalize inputs (backend now sends RAW dollars) === */
  const labelsAll = payload?.charts?.overview?.labels || [];
  const series    = payload?.charts?.overview?.series || [];
  const map = Object.fromEntries((series || []).map(d => [String(d.key || d.label).toLowerCase(), d.data || []]));

  const labels = labelsAll.slice(-12);
  const rev    = (map.revenue      || []).slice(-12).map(Number);
  const exp    = (map.expenses     || []).slice(-12).map(Number);
  const net    = (map.profit       || map.netincome || []).slice(-12).map(Number);

  const chartCfg = useMemo(() => toComboChart(labels, rev, exp, net), [labels, rev, exp, net]);
  // Title adapts to viewport: 6 months on mobile, 12 on desktop
  let isMobile = false;
  try {
    isMobile = (typeof window !== 'undefined') && window.matchMedia('(max-width: 600px)').matches;
  } catch (_) { /* no-op */ }
  const rangeLabel = isMobile ? 'last 6 months' : 'last 12 months';


  /* KPI strip (prefer backend KPIs, fallback to series) */
  const freq = String(payload?.context?.frequency || 'quarterly').toLowerCase();
  const yoyPeriods = freq === 'monthly' ? 12 : 4;

  const last   = (arr) => (Array.isArray(arr) && arr.length ? arr[arr.length - 1] : null);
  const lastYoY = (arr, p = yoyPeriods) => {
    if (!Array.isArray(arr) || arr.length < p + 1) return null;
    const a = Number(arr[arr.length - 1]); const b = Number(arr[arr.length - 1 - p]);
    if (!isFinite(a) || !isFinite(b) || b === 0) return null;
    return ((a - b) / b) * 100;
  };

  const kpiMap = Object.fromEntries((payload?.kpis || []).map(k => [k.key, k]));
  const fbRev = { value: last(rev), deltaYoY: lastYoY(map.revenue) };
  const fbExp = { value: last(exp), deltaYoY: lastYoY(map.expenses) };
  const fbNet = { value: last(net), deltaYoY: lastYoY(map.profit || map.netincome) };

  const kpis = [
    { label: 'Total Revenue',  value: kpiMap.totalRevenue?.value ?? fbRev.value,   isExpense: false, deltaYoY: kpiMap.totalRevenue?.deltaYoY ?? fbRev.deltaYoY },
    { label: 'Total Expenses', value: kpiMap.totalExpenses?.value ?? fbExp.value,  isExpense: true,  deltaYoY: kpiMap.totalExpenses?.deltaYoY ?? fbExp.deltaYoY },
    { label: 'Net Income',     value: kpiMap.netIncome?.value ?? fbNet.value,      isExpense: false, deltaYoY: kpiMap.netIncome?.deltaYoY ?? fbNet.deltaYoY },
  ];

  /* Burn & Runway
     If backend provides runway.{burnRate, months}, use it.
     Otherwise, estimate monthly burn from series (Expenses - Revenue) and
     runway months from any available cash KPI. */
  const providedBurn  = Number(payload?.runway?.burnRate);
  const providedMonths = Number(payload?.runway?.months);

  let burnRate = Number.isFinite(providedBurn) ? providedBurn : 0;
  let runwayMonths = Number.isFinite(providedMonths) ? providedMonths : 0;

  if (!Number.isFinite(providedBurn)) {
    // Fallback: use recent periods to estimate average monthly burn
    // Monthly: average of last up to 3 months
    // Quarterly: average of last up to 2 quarters, then /3 to monthly
    const periods = freq === 'monthly' ? Math.min(3, labels.length) : Math.min(2, labels.length);
    if (periods > 0) {
      const r = rev.slice(-periods).reduce((a, b) => a + (Number(b) || 0), 0);
      const e = exp.slice(-periods).reduce((a, b) => a + (Number(b) || 0), 0);
      let avgPerPeriod = (e - r) / periods;           // positive = burn
      if (freq !== 'monthly') avgPerPeriod = avgPerPeriod / 3; // convert quarters → months
      burnRate = Math.max(0, avgPerPeriod);           // don’t show negative “burn”
    }
  }

  if (!Number.isFinite(providedMonths)) {
    // Try to compute runway from any cash-like KPI if burn > 0
    const kpisByKey = Object.fromEntries((payload?.kpis || []).map(k => [String(k.key || '').toLowerCase(), k]));
    const cashKeys = ['cash', 'cashonhand', 'cashbalance', 'endingcash'];
    let cashOnHand = 0;
    for (const k of cashKeys) {
      const v = Number(kpisByKey[k]?.value);
      if (Number.isFinite(v) && v > 0) { cashOnHand = v; break; }
    }
    runwayMonths = (burnRate > 0 && cashOnHand > 0) ? Math.floor(cashOnHand / burnRate) : 0;
  }
  // Determine if dataset is effectively profitable (revenue >= expenses on average recently)
  const periodsForNet = freq === 'monthly' ? Math.min(3, labels.length) : Math.min(2, labels.length);
  let avgNetIncomeRecent = null;
  if (periodsForNet > 0) {
    const r = rev.slice(-periodsForNet).reduce((a, b) => a + (Number(b) || 0), 0);
    const e = exp.slice(-periodsForNet).reduce((a, b) => a + (Number(b) || 0), 0);
    avgNetIncomeRecent = (r - e) / periodsForNet; // positive => profitable
  }
  const isProfitable = Number.isFinite(avgNetIncomeRecent) && avgNetIncomeRecent >= 0;

  // If profitable, force a friendly display (no burn/runway)
  if (isProfitable) {
    burnRate = 0;
    // runwayMonths will be shown as N/A when burnRate === 0
  }



  /* Composition (already filtered server-side; top-5 + Other) */
  const revMix = payload?.charts?.composition?.revenueMix || [];
  const expMix = payload?.charts?.composition?.spend      || [];

  const revH = useMemo(() => toHBar(revMix.map(d => d.name), revMix.map(d => Number(d.value) || 0)), [revMix]);
  const expH = useMemo(() => toHBar(expMix.map(d => d.name), expMix.map(d => Number(d.value) || 0)), [expMix]);

  return (
    <div className="dashboard-compact" role="region" aria-label="Finance dashboard">
      {/* Row 1 — KPIs */}
      <div className="row-kpis">
        {kpis.map((k) => (
          <KPIBox
            key={k.label}
            label={k.label}
            value={k.value}
            isExpense={k.isExpense}
            deltaYoY={k.deltaYoY}
          />
        ))}
      </div>

      {/* Row 2 — Income statement */}
      <div className="row-main card">
        <h3 className="muted">Income statement ({rangeLabel})</h3>
        <div className="main-chart">
          <div className="chart-body">
            <Chart type="bar" data={chartCfg.data} options={chartCfg.options} />
          </div>
        </div>
      </div>

      {/* Row 3 — Burn / Runway */}
      <div className="row-burn-runway">
        <div className="card">
          <h3 className="muted">Burn (recent periods)</h3>
          <div className="hero-number" style={{fontVariantNumeric:'tabular-nums'}}>
            {burnRate > 0
              ? (<>{formatCurrencyShort(burnRate)} <span className="muted">/ mo</span></>)
              : ('No burn')}
          </div>
          <div className="hero-sub">
            {burnRate > 0 ? 'Estimated from Revenue and Expenses' : 'Profitable: revenue exceeds expenses'}
          </div>
        </div>

        <div className="card">
          <h3 className="muted">Runway</h3>
          <div className="hero-number" style={{fontVariantNumeric:'tabular-nums'}}>
            {burnRate > 0
              ? (isFinite(runwayMonths) ? `${runwayMonths} months` : '—')
              : 'N/A'}
          </div>
          <div className="hero-sub">
            {burnRate > 0 ? 'At current spend' : 'Not applicable (no burn)'}
          </div>
        </div>
      </div>

      {/* Row 4 — Mix charts */}
      <div className="row-mixes">
        <div className="card mix-chart">
          <h3 className="muted">Where revenue comes from</h3>
          <div className="mix-body">
            {isMobile ? (
              <PieChartComponent data={revMix} />
            ) : (
              <Chart type="bar" data={revH.data} options={revH.options} />
            )}
          </div>

        </div>
        <div className="card mix-chart">
          <h3 className="muted">Where your expenses go</h3>
          <div className="mix-body">
            {isMobile ? (
              <PieChartComponent data={expMix} />
            ) : (
              <Chart type="bar" data={expH.data} options={expH.options} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
